/**
 * Backend timestamps (measurement_timestamp, cabinet_status.last_measurement,
 * alert timestamps, ...) are naive UTC ISO strings WITHOUT a timezone suffix
 * (Postgres TIMESTAMP WITHOUT TIME ZONE, e.g. "2026-07-04T13:36:53.567824").
 * `new Date(s)` parses such strings as LOCAL time, which skews every displayed
 * age/online check by the UTC offset (e.g. +2 h in CEST). Parse them as UTC.
 */
export function parseUtcTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  // Already has explicit timezone info ("Z" or a +hh:mm / -hh:mm offset)?
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a backend naive-UTC timestamp as a local-time string. Centralizes the
 * "parse as UTC, display as local" rule so display sites can't regress to a raw
 * `new Date(s)` (which parses naive-UTC as local and skews by the UTC offset).
 * Returns '' for null/unparseable input.
 */
export function formatLocalTimestamp(
  value: string | null | undefined,
  locales?: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions
): string {
  return parseUtcTimestamp(value)?.toLocaleString(locales, options) ?? '';
}
